import { useEffect, useMemo, useState } from "react";
import { Button } from "@common";
import { useInvisalignInvoices, useLabInvoiceAttachments, useReportFiles } from "@api/hooks";
import { canViewLabInvoices } from "@utils/roles";
import DailyReportFiles from "./DailyReportFiles";
import LabInvoiceAttachments from "./LabInvoiceAttachments";

/**
 * The day's attachments, in one panel with a tab per kind.
 *
 * Files and Lab Invoices used to be two stacked sections with identical chrome,
 * which read as one thing rendered twice and cost a lot of vertical space above
 * the report form. They are genuinely different — files are inert paperwork,
 * lab invoices drive costs and are role-gated — so they stay separate lists
 * behind tabs rather than being merged into one strip.
 *
 * The panel owns the counts and the Upload button so the tab row is the single
 * place anything about attachments happens; each tab's component renders its own
 * body in `embedded` mode.
 *
 * Invisalign Invoices is plain storage like Files (same cards, same access), but
 * its own list and GCS folder so those invoices never mix into the day's Files.
 */
export default function DailyAttachmentsPanel({
  clinicId,
  date,
  role,
  canAssignLabInvoices = false,
  selectedAttachmentId = null,
  onSelectAttachment,
  selectedPractitioner = null,
}) {
  const showLabInvoices = canViewLabInvoices(role);
  const [activeTab, setActiveTab] = useState("files");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isInvisalignUploadOpen, setIsInvisalignUploadOpen] = useState(false);

  // Both queries share their cache with the tab bodies, so the counts on the
  // inactive tab cost no extra request.
  const { data: filesData } = useReportFiles({ clinic: clinicId, date });
  const { data: invisalignData } = useInvisalignInvoices({ clinic: clinicId, date });
  const { data: labData } = useLabInvoiceAttachments(
    { clinic: clinicId, date },
    { enabled: showLabInvoices && Boolean(clinicId) && Boolean(date) },
  );

  const fileCount = filesData?.results?.length ?? 0;
  const invisalignCount = invisalignData?.results?.length ?? 0;
  const labCount = useMemo(() => labData?.results?.length ?? 0, [labData?.results]);

  // Selecting a lab invoice opens the PDF preview beside the form, so the tab
  // holding that selection must be the visible one — otherwise the preview is
  // open with no sign of what it belongs to.
  useEffect(() => {
    if (selectedAttachmentId && showLabInvoices) setActiveTab("labInvoices");
  }, [selectedAttachmentId, showLabInvoices]);

  const tabs = [
    { key: "files", label: "Files", count: fileCount },
    { key: "invisalign", label: "Invisalign Invoices", count: invisalignCount },
    ...(showLabInvoices ? [{ key: "labInvoices", label: "Lab Invoices", count: labCount }] : []),
  ];

  return (
    <section className="mb-5 rounded-lg border border-palette-slate-divider bg-palette-slate-soft px-4 py-3">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2 border-b border-palette-slate-divider">
        <div className="flex items-end gap-1">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                // -1px bottom margin so the active tab's underline sits on the
                // container's border rather than above it.
                className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "border-palette-indigo-label text-palette-indigo-badge"
                    : "border-transparent text-palette-slate-secondary hover:text-palette-slate-primary"
                }`}
              >
                {tab.label}
                <span
                  className={`ml-1.5 rounded px-1.5 py-0.5 text-[11px] ${
                    isActive
                      ? "bg-palette-indigo-soft text-palette-indigo-badge"
                      : "bg-palette-gray-subtle text-palette-gray-secondary"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 pb-2">
          <span className="text-xs text-palette-slate-secondary">
            {clinicId} · {date}
          </span>
          {activeTab === "files" && (
            <Button type="button" variant="primary" size="sm" onClick={() => setIsUploadOpen(true)}>
              Upload files
            </Button>
          )}
          {activeTab === "invisalign" && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => setIsInvisalignUploadOpen(true)}
            >
              Upload invoices
            </Button>
          )}
          {activeTab === "labInvoices" && selectedAttachmentId && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onSelectAttachment?.(null, null)}
            >
              Close preview
            </Button>
          )}
        </div>
      </div>

      {/* Both bodies stay mounted: the lab-invoice list holds the preview
          selection, and unmounting it on a tab switch would drop that. */}
      <div className={activeTab === "files" ? "" : "hidden"}>
        <DailyReportFiles
          clinicId={clinicId}
          date={date}
          isUploadOpen={isUploadOpen}
          onUploadOpenChange={setIsUploadOpen}
        />
      </div>

      <div className={activeTab === "invisalign" ? "" : "hidden"}>
        <DailyReportFiles
          kind="invisalign"
          clinicId={clinicId}
          date={date}
          isUploadOpen={isInvisalignUploadOpen}
          onUploadOpenChange={setIsInvisalignUploadOpen}
        />
      </div>

      {showLabInvoices && (
        <div className={activeTab === "labInvoices" ? "" : "hidden"}>
          <LabInvoiceAttachments
            embedded
            clinicId={clinicId}
            date={date}
            canAssign={canAssignLabInvoices}
            selectedPractitioner={selectedPractitioner}
            selectedAttachmentId={selectedAttachmentId}
            onSelect={onSelectAttachment}
          />
        </div>
      )}
    </section>
  );
}
